/**
 * Covers des défis live (migration 0658) : 6 images prédéfinies (bucket public
 * `live-covers/presets/`) ou une image importée par l'organisateur
 * (`live-covers/<son id>/<uuid>.<ext>`). On stocke un CHEMIN dans le bucket,
 * jamais une URL (`live_challenges.cover_path`). Sans cover choisie, l'app en
 * prend une parmi les presets selon le défi (toujours la même pour un défi).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { myPrivateLivesKey, privateLiveKey, warmPrivateLivePreview } from '@/lib/private-live';

// RPC postérieure aux types générés → client générique.
const db = supabase as unknown as SupabaseClient;

export const LIVE_COVER_BUCKET = 'live-covers';
const SUPABASE_URL =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) ??
  'https://wioisrgjopdtpczliwkn.supabase.co';

export interface LiveCoverPreset {
  key: string;
  label: string;
}

/** Les 6 ambiances (fichiers `presets/<clé>.webp` et `presets/thumbs/<clé>.webp`). */
export const LIVE_COVER_PRESETS: readonly LiveCoverPreset[] = [
  { key: 'arene', label: 'L’arène' },
  { key: 'duel-bleu', label: 'Duel bleu' },
  { key: 'duel-or', label: 'Duel doré' },
  { key: 'classe-soleil', label: 'Classe au soleil' },
  { key: 'salle-de-classe', label: 'Salle de classe' },
  { key: 'cahier', label: 'Le cahier' },
];

/** Miroir du CHECK SQL `live_challenges_cover_path_check`. */
export const PRESET_PATH_RE = /^presets\/[a-z0-9-]{2,40}\.webp$/;
export const UPLOAD_PATH_RE = /^[0-9a-f-]{36}\/[0-9a-f-]{36}\.(webp|jpg|png)$/;

export const LIVE_COVER_MAX_BYTES = 5 * 1024 * 1024;
export const LIVE_COVER_MAX_WIDTH = 1600;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

export function presetPath(key: string): string {
  return `presets/${key}.webp`;
}

export function presetThumbPath(key: string): string {
  return `presets/thumbs/${key}.webp`;
}

/** Clé d'un preset depuis son chemin (« presets/arene.webp » → « arene »), sinon null. PUR. */
export function presetKeyOfPath(path: string | null | undefined): string | null {
  if (!path || !PRESET_PATH_RE.test(path)) return null;
  const key = path.slice('presets/'.length, -'.webp'.length);
  return LIVE_COVER_PRESETS.some((p) => p.key === key) ? key : null;
}

/** Chemin accepté par le serveur (preset connu ou image importée). PUR. */
export function isValidCoverPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return presetKeyOfPath(path) !== null || UPLOAD_PATH_RE.test(path);
}

/** URL publique d'un fichier du bucket. PUR. */
export function liveCoverPublicUrl(path: string, base: string = SUPABASE_URL): string {
  return `${base.replace(/\/$/, '')}/storage/v1/object/public/${LIVE_COVER_BUCKET}/${path}`;
}

/** Hash FNV-1a 32 bits (stable d'un appareil à l'autre). PUR. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Preset par défaut d'un défi : toujours le même pour un même identifiant. PUR. */
export function defaultCoverKey(seed: string | null | undefined): string {
  const list = LIVE_COVER_PRESETS;
  if (!seed) return list[0]!.key;
  return list[fnv1a(seed) % list.length]!.key;
}

/**
 * URL de la cover à afficher : chemin choisi, sinon l'image de la campagne ou
 * de l'exercice (`fallbackUrl`), sinon un preset tiré du défi. `thumb` : la
 * vignette d'un preset (480 px) pour les petites cartes. PUR.
 */
export function liveCoverUrl(opts: {
  path?: string | null;
  fallbackUrl?: string | null;
  seed?: string | null;
  thumb?: boolean;
  base?: string;
}): string {
  const base = opts.base ?? SUPABASE_URL;
  const preset = presetKeyOfPath(opts.path);
  if (preset)
    return liveCoverPublicUrl(opts.thumb ? presetThumbPath(preset) : presetPath(preset), base);
  if (opts.path && UPLOAD_PATH_RE.test(opts.path)) return liveCoverPublicUrl(opts.path, base);
  if (opts.fallbackUrl) return opts.fallbackUrl;
  const key = defaultCoverKey(opts.seed);
  return liveCoverPublicUrl(opts.thumb ? presetThumbPath(key) : presetPath(key), base);
}

/** Preset tiré au hasard (choix proposé par défaut à la création). */
export function randomPresetPath(rand: () => number = Math.random): string {
  const list = LIVE_COVER_PRESETS;
  const i = Math.min(list.length - 1, Math.floor(rand() * list.length));
  return presetPath(list[i]!.key);
}

/** Message d'erreur d'un fichier refusé avant l'envoi, sinon null. PUR. */
export function coverFileError(file: { type: string; size: number }): string | null {
  // Type vide (certains Android, HEIC) : on tente de décoder l'image.
  if (file.type && !file.type.startsWith('image/')) {
    return 'Choisis une image (JPEG, PNG ou WebP).';
  }
  return null;
}

/** Taille réduite d'une image trop large (largeur max 1600 px). PUR. */
export function fitWithin(
  width: number,
  height: number,
  max: number = LIVE_COVER_MAX_WIDTH,
): { width: number; height: number } {
  if (width <= max) return { width, height };
  return { width: max, height: Math.round((height * max) / width) };
}

/**
 * Image prête à envoyer : réduite à 1600 px de large, en WebP (JPEG si le
 * navigateur ne sait pas écrire de WebP). Une photo de téléphone (souvent
 * > 5 Mo, parfois en HEIC) passe ainsi sous la limite du bucket.
 */
async function prepareCoverImage(file: File): Promise<{ blob: Blob; ext: 'webp' | 'jpg' | 'png' }> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = fitWithin(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no_canvas');
    ctx.drawImage(bitmap, 0, 0, size.width, size.height);
    bitmap.close?.();
    const toBlob = (type: string) =>
      new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.85));
    const webp = await toBlob('image/webp');
    if (webp && webp.type === 'image/webp') return { blob: webp, ext: 'webp' };
    const jpeg = await toBlob('image/jpeg');
    if (jpeg) return { blob: jpeg, ext: 'jpg' };
    throw new Error('no_blob');
  } catch {
    // Navigateur sans createImageBitmap / canvas : on envoie l'original.
    if (!ALLOWED_MIME.includes(file.type)) throw new Error('Format d’image non pris en charge.');
    if (file.size > LIVE_COVER_MAX_BYTES) throw new Error('Image trop lourde (5 Mo au plus).');
    const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    return { blob: file, ext };
  }
}

/** Importe une image dans le dossier de l'utilisateur et renvoie son chemin. */
export async function uploadLiveCover(userId: string, file: File): Promise<string> {
  const issue = coverFileError(file);
  if (issue) throw new Error(issue);
  const { blob, ext } = await prepareCoverImage(file);
  if (blob.size > LIVE_COVER_MAX_BYTES) throw new Error('Image trop lourde (5 Mo au plus).');
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;
  const contentType = ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg';
  const { error } = await supabase.storage
    .from(LIVE_COVER_BUCKET)
    .upload(path, blob, { contentType, upsert: false, cacheControl: '31536000' });
  if (error) throw new Error('L’image n’a pas pu être envoyée. Réessaie.');
  return path;
}

const COVER_ERRORS: Record<string, string> = {
  not_host: 'Seul l’organisateur peut changer la cover.',
  bad_cover: 'Cette image ne peut pas servir de cover.',
  unknown_cover: 'Image introuvable. Importe-la de nouveau.',
  not_found: 'Ce défi n’existe plus.',
};

/** Message lisible d'une erreur de `set_private_live_cover`. PUR. */
export function coverErrorMessage(raw: string | null | undefined): string {
  const code = Object.keys(COVER_ERRORS).find((k) => raw?.includes(k));
  return (code && COVER_ERRORS[code]) || 'La cover n’a pas pu être enregistrée.';
}

/** Choisir la cover d'un défi privé (organisateur ou admin). */
export async function setPrivateLiveCover(challengeId: string, path: string | null): Promise<void> {
  const { error } = await db.rpc('set_private_live_cover', {
    p_challenge: challengeId,
    p_path: path,
  });
  if (error) throw new Error(coverErrorMessage(error.message));
}

export function useSetPrivateLiveCover(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { challengeId: string; path: string | null }) =>
      setPrivateLiveCover(input.challengeId, input.path),
    // Nouvelle cover = nouvelle image d'aperçu : la préparer tout de suite.
    onSuccess: () => warmPrivateLivePreview(code),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: privateLiveKey(code) });
      void qc.invalidateQueries({ queryKey: myPrivateLivesKey });
    },
  });
}
